/**
 * Import Today's Recordings — Universal Intent UI-S3 Multi-Camera Intake.
 *
 * Governed by:
 *   BLUEPRINT_UI_S3_MULTI_CAMERA_INTAKE.md r1
 *   BLUEPRINT_UNIVERSAL_INTENT.md R3 · §1B · §4 · §12
 *   GC60_DAY_ZERO_FIRST_LIGHT_PROGRAMME_DIRECTION_R1.md §8.4
 *
 * Constitutional guardrails observed here:
 *   • Every user-visible string comes from the backend narrator (single
 *     voice — Universal Intent). This component MUST NOT invent its own
 *     narration; it displays the strings the server returns.
 *   • Enumeration is client-side (iPad Safari cannot read raw SD cards).
 *     The Founder taps `<input type=file multiple>` and picks the mounted
 *     SD-card contents from the Files app; drag-and-drop is a fallback
 *     for capable devices/browsers.
 *   • SHA-256 is computed client-side per file (Blueprint §3 step 1).
 *   • The full batch flow: init → enumerate → chunk-upload → finalize →
 *     complete. Progress is a live percentage rendered in Universal
 *     Intent's voice.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { isIOSSafari } from "../lib/iosDetect";
import { takePendingFiles } from "../lib/pendingFiles";
import LearningVideoReview from "../components/learner/LearningVideoReview";
import {
  createExternalMediaJob,
  getLearningUploadContext,
  presignExternalMediaPart,
  finalizeExternalMediaJob,
  putPartDirectToR2,
} from "../lib/externalMediaUpload";


/* ------------------------------------------------------------------ */
/* iOS Safari picker rules (see MobilePhotosIntake.jsx doctrine):     */
/*   • `accept` MUST be omitted on iOS Safari — the                    */
/*     hidden+accept+multiple combo is the "unstable picker shape"    */
/*     that causes iOS to return only ONE file even when the Founder  */
/*     ticked several. Omitting `accept` while keeping `multiple`     */
/*     is the probe-proven safe shape.                                 */
/*   • The input must NOT be `display:none` — some iOS versions treat */
/*     display:none file inputs as non-interactive. Use offscreen 1px */
/*     sizing (still visually hidden, still tappable).                */
/*   • iOS Safari sometimes fires `cancel` even when files were       */
/*     picked; `input.files` is the source of truth, not the event.   */
/* ------------------------------------------------------------------ */
const IOS_SAFE = typeof window !== "undefined" && isIOSSafari();
const DESKTOP_ACCEPT = "video/*,audio/*,image/*,.mp4,.mov,.m4v,.avi,.mkv,.webm,.ts,.mts,.m2ts,.mxf,.insv,.mp3,.m4a,.wav,.aac,.flac,.ogg,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.bmp,.tif,.tiff,.dng";

/* ------------------------------------------------------------------ */
/* SHA-256 utility · uses WebCrypto (available on iPad Safari 15+).   */
/* ------------------------------------------------------------------ */
async function sha256OfBlob(blob) {
  const buf = await blob.arrayBuffer();
  const digest = await window.crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/* ------------------------------------------------------------------ */
/* Recording row · pure UI, no narration.                              */
/* ------------------------------------------------------------------ */
function RecordingRow({ item }) {
  const label = item.status || "pending";
  const pct = typeof item.percent === "number" ? item.percent : 0;
  return (
    <li data-testid={`ui-s3-recording-row-${item.sha256 || item.tempId}`} className="ui-s3-row">
      <div className="ui-s3-row__meta">
        <span className="ui-s3-row__name" data-testid="ui-s3-row-name">{item.filename}</span>
        <span className="ui-s3-row__size" data-testid="ui-s3-row-size">{formatBytes(item.byte_size)}</span>
      </div>
      <div className="ui-s3-row__status">
        <span data-testid="ui-s3-row-status">{label}</span>
        {label === "uploading" && (
          <div className="ui-s3-progressbar" aria-label="upload progress">
            <div className="ui-s3-progressbar__fill" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </li>
  );
}

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${Math.round(n / (1024 * 1024))} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/* ------------------------------------------------------------------ */
/* Main component                                                       */
/* ------------------------------------------------------------------ */
export default function ImportTodaysRecordings() {
  const [searchParams] = useSearchParams();
  const purposeCode = searchParams.get("purpose") || "sd_card_teaching_day_intake";
  const purposeLabel = searchParams.get("label") || "Import Today's Recordings";
  const learningContextId = searchParams.get("learning_context_id") || null;
  const [learningContext, setLearningContext] = useState(null);
  const [contextError, setContextError] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | selecting | enumerating | confirming | importing | complete | error
  const [selectedFiles, setSelectedFiles] = useState([]); // File objects
  const [rows, setRows] = useState([]);       // enumeration + upload state per file
  const [narration, setNarration] = useState("Ready when you are — tap 'Import Today's Recordings' and pick the recordings on the SD card.");
  const [summary, setSummary] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const inFlightRef = useRef(false); // guards against iOS Safari double-fire (input + change + cancel)
  const importInFlightRef = useRef(false);

  useEffect(() => {
    let current = true;
    setLearningContext(null); setContextError("");
    if (learningContextId) {
      getLearningUploadContext(learningContextId).then((context) => {
        if (current) setLearningContext(context);
      }).catch(() => {
        if (current) setContextError("We couldn't confirm whose learning recording this is. Return to Training Mode and try again.");
      });
    }
    return () => { current = false; };
  }, [learningContextId]);

  const setUiVoice = useCallback((line) => {
    if (line && typeof line === "string") setNarration(line);
  }, []);

  /* iOS Safari sometimes fires the native `cancel` event on the file
   * input even when the Founder actually picked files. React does not
   * expose `onCancel` for <input>, so we bind it natively here. If
   * files exist on the input at cancel time, treat them as picked. */
  useEffect(() => {
    const el = fileInputRef.current;
    if (!el) return undefined;
    const onNativeCancel = () => {
      if (el.files && el.files.length > 0) {
        // eslint-disable-next-line no-use-before-define
        handleFilesPickedRef.current(el.files);
      }
    };
    el.addEventListener("cancel", onNativeCancel);
    return () => el.removeEventListener("cancel", onNativeCancel);
  }, []);
  const handleFilesPickedRef = useRef(null);

  /* GC63 · 2026-07-26 — cross-device parity handoff. If the Founder
   * dropped or picked files on the /upload central dropzone and then
   * chose a purpose tile, those File objects were stashed via
   * lib/pendingFiles. Consume them here on mount so import kicks off
   * immediately — no re-picking on laptop. */
  useEffect(() => {
    const pending = takePendingFiles();
    if (pending && pending.length > 0) {
      // fire-and-forget; handleFilesPicked handles phase transitions
      // eslint-disable-next-line no-use-before-define
      handleFilesPickedRef.current?.(pending);
    }
  }, []);

  /* -------- Step 1 · File selection + local fingerprinting ---------- */
  const handleFilesPicked = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const arr = Array.from(fileList);
    setSelectedFiles(arr);
    setPhase("enumerating");
    setUiVoice(
      arr.length === 1
        ? "Checking that file — one moment…"
        : `Checking those ${arr.length} files — one moment…`,
    );

    try {
      const nextRows = [];
      for (let index = 0; index < arr.length; index += 1) {
        const file = arr[index];
        // Fingerprint only. No media bytes are sent to Main NDG here.
        // eslint-disable-next-line no-await-in-loop
        const sha256 = await sha256OfBlob(file);
        const suffix = (window.crypto?.randomUUID?.() || `${Date.now()}-${index}`)
          .replace(/[^A-Za-z0-9_-]/g, "");
        nextRows.push({
          file_index: index,
          filename: file.name,
          byte_size: file.size,
          sha256,
          mime_type: file.type || guessMime(file.name),
          capture_date_iso: file.lastModified
            ? new Date(file.lastModified).toISOString()
            : null,
          external_media_id: `ndg-${sha256.slice(0, 24)}-${suffix}`.slice(0, 128),
          status: "ready",
          percent: 0,
        });
      }

      setRows(nextRows);
      setSummary(null);
      setPhase(nextRows.length > 0 ? "confirming" : "idle");
      setUiVoice(
        nextRows.length === 1
          ? "That file is ready to import."
          : `${nextRows.length} files are ready to import.`,
      );
    } catch (err) {
      setPhase("error");
      setUiVoice("I couldn't prepare those files. Please try again.");
    } finally {
      inFlightRef.current = false;
    }
  }, [setUiVoice]);

  // Keep the ref pointing at the current handler so the native
  // `cancel` listener (bound once) always calls the latest closure.
  handleFilesPickedRef.current = handleFilesPicked;

  /* -------- Step 2 · External controller → direct R2 → finalize --- */
  const confirmAndImport = useCallback(async () => {
    if (importInFlightRef.current || (learningContextId && (!learningContext || contextError))) return;
    importInFlightRef.current = true;
    setPhase("importing");
    setUiVoice("Right — sending these straight to external storage now.");

    const PART_SIZE = 25 * 1024 * 1024;
    const filesToImport = rows.filter(
      (row) => row.status === "ready" || row.status === "upload interrupted",
    );
    let importedCount = rows.filter((row) => row.status === "imported").length;
    let refusedCount = 0;

    for (let i = 0; i < filesToImport.length; i += 1) {
      const row = filesToImport[i];
      const file = selectedFiles[row.file_index];
      if (!file) {
        refusedCount += 1;
        setRows((prev) => prev.map((item) => (
          item.external_media_id === row.external_media_id
            ? { ...item, status: "refused (file not found in selection)" }
            : item
        )));
        // eslint-disable-next-line no-continue
        continue;
      }

      const totalParts = Math.max(1, Math.ceil(file.size / PART_SIZE));
      const ndgMediaId = row.external_media_id;

      setRows((prev) => prev.map((item) => (
        item.external_media_id === row.external_media_id
          ? { ...item, status: "uploading", percent: 0 }
          : item
      )));

      let uploadedBytes = 0;
      let uploadFailed = false;

      try {
        // Small JSON control-plane request only. Raw file bytes never go here.
        // eslint-disable-next-line no-await-in-loop
        const created = await createExternalMediaJob({
          ndgMediaId,
          file,
          totalParts,
          purposeCode: learningContextId ? "lesson_evidence" : purposeCode,
          learningContextId,
          fingerprint: row.sha256,
          sourceDevice: IOS_SAFE ? "ios_safari" : "browser",
        });
        if (learningContextId && created?.learning_context?.id !== learningContextId) {
          throw new Error("Learning context was not confirmed by the upload service");
        }

        // A lost completion receipt can be recovered without sending bytes again.
        let finished = created;
        if (created?.job?.state !== "completed") {
          for (let idx = 0; idx < totalParts; idx += 1) {
            const start = idx * PART_SIZE;
            const end = Math.min(start + PART_SIZE, file.size);
            const part = file.slice(start, end);

            // eslint-disable-next-line no-await-in-loop
            const signed = await presignExternalMediaPart(ndgMediaId, idx + 1);

            // RAW BYTES: browser/device -> signed R2 URL. No NDG backend relay.
            // eslint-disable-next-line no-await-in-loop
            await putPartDirectToR2(signed.upload_url, part, (evt) => {
              const loaded = Math.min(evt.loaded || 0, end - start);
              const overallPct = Math.round(
                ((uploadedBytes + loaded) / file.size) * 100,
              );
              setRows((prev) => prev.map((item) => (
                item.external_media_id === row.external_media_id
                  ? { ...item, percent: Math.min(99, overallPct) }
                  : item
              )));
            });

            uploadedBytes += end - start;
          }

          // eslint-disable-next-line no-await-in-loop
          finished = await finalizeExternalMediaJob(ndgMediaId);
        }
        if (learningContextId && finished?.learning_context?.id !== learningContextId) {
          throw new Error("Learning context was not retained on completion");
        }
        const finalState = finished?.job?.state;
        if (finalState !== "completed") {
          throw new Error(`unexpected external media state: ${finalState}`);
        }

        importedCount += 1;
        setRows((prev) => prev.map((item) => (
          item.external_media_id === row.external_media_id
            ? { ...item, status: "imported", percent: 100 }
            : item
        )));
      } catch (err) {
        uploadFailed = true;
      }

      if (uploadFailed) {
        refusedCount += 1;
        setRows((prev) => prev.map((item) => (
          item.external_media_id === row.external_media_id
            ? { ...item, status: "upload interrupted" }
            : item
        )));
      }
    }

    setSummary({
      imported_count: importedCount,
      duplicate_count: 0,
      refused_count: refusedCount,
    });
    setPhase("complete");
    importInFlightRef.current = false;

    if (refusedCount > 0) {
      setUiVoice(
        `${importedCount} imported. ${refusedCount} still need attention — the originals have not been moved through the main NDG system.`,
      );
    } else {
      setUiVoice(
        importedCount === 1
          ? "Grand. That file is safely filed in external storage."
          : `Grand. All ${importedCount} files are safely filed in external storage.`,
      );
    }
  }, [purposeCode, rows, selectedFiles, setUiVoice, learningContextId, learningContext, contextError]);

  /* -------- Cancel/reset local selection -------------------------- */
  const cancelBatch = useCallback(async () => {
    setPhase("idle");
    setRows([]);
    setSelectedFiles([]);
    setSummary(null);
    inFlightRef.current = false;
    if (fileInputRef.current) {
      try { fileInputRef.current.value = ""; } catch (e) { /* ignore */ }
    }
    setUiVoice("Ready when you are — tap 'Import Today's Recordings' and pick the recordings on the SD card.");
  }, [setUiVoice]);

  /* -------- Drag-and-drop fallback --------------------------------- */
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      handleFilesPicked(dt.files);
    }
  }, [handleFilesPicked]);

  const onDragOver = useCallback((e) => { e.preventDefault(); setDragging(true); }, []);
  const onDragLeave = useCallback(() => setDragging(false), []);

  return (
    <main data-testid="ui-s3-import-page" className="ui-s3-page">
      <header className="ui-s3-header">
        <Link to="/upload" data-testid="ui-s3-back-link" className="ui-s3-back">← Universal Upload</Link>
        <h1 data-testid="ui-s3-page-title">{purposeLabel}</h1>
      </header>

      {learningContextId && <section className="ui-s3-narration" data-testid="learning-upload-context">
        {contextError ? <p role="alert">{contextError}</p> : learningContext ? <p>
          Learning focus: {learningContext.competency_label} — {learningContext.current_focus_responsibility_id.replace(/_/g, " ")}. Your reflection stays attached. Recording analysis is advisory and cannot award independent passes.
        </p> : <p role="status">Checking your learning context…</p>}
        <Link to="/training">Return to Training Mode</Link>
      </section>}

      {/* Universal Intent narration — the ONE voice */}
      <section
        role="status"
        aria-live="polite"
        data-testid="ui-s3-narration"
        className="ui-s3-narration"
      >
        {narration}
      </section>

      {/* Drop-target / file picker card */}
      {(phase === "idle" || phase === "error") && (
        <section
          className={`ui-s3-drop ${dragging ? "ui-s3-drop--active" : ""}`}
          data-testid="ui-s3-drop-zone"
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          <p className="ui-s3-drop__hint">
            On iPad, tap the button below then pick every recording you want in
            one go — tap <strong>Select</strong> in the Files app first, then
            tap each clip (or use two-finger drag) to tick them all before
            hitting <strong>Open</strong>. SD-card contents mounted via the USB-C
            Camera Adapter appear in the Files sidebar. Drag-and-drop works on
            desktop and iPadOS 15+.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            /* iOS-safe shape (May 22, 2026 doctrine, see
             * MobilePhotosIntake.jsx): OMIT `accept` on iOS Safari.
             * On desktop / Android, keep the accept-list for a tight
             * picker UX. `capture` is intentionally NEVER set — that
             * flag forces camera capture and disables multi-file
             * selection on iOS. */
            {...(IOS_SAFE ? {} : { accept: DESKTOP_ACCEPT })}
            /* Offscreen 1px input rather than display:none — some
             * iOS versions refuse to activate display:none inputs
             * even via programmatic .click(). */
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: "none",
              overflow: "hidden",
            }}
            aria-hidden="true"
            tabIndex={-1}
            onChange={(e) => handleFilesPicked(e.target.files)}
            /* onInput is a belt-and-braces catch for iOS Safari
             * variants that fire `input` before `change`. */
            onInput={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFilesPicked(e.target.files);
              }
            }}
            data-testid="ui-s3-file-input"
            data-multiple="true"
            data-ios-safe={IOS_SAFE ? "true" : "false"}
          />
          <button
            type="button"
            className="ui-s3-cta"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            data-testid="ui-s3-open-picker-btn"
          >
            Import Today&apos;s Recordings
          </button>
        </section>
      )}

      {/* Confirmation card */}
      {phase === "confirming" && (
        <section className="ui-s3-confirm" data-testid="ui-s3-confirm-card">
          <ul className="ui-s3-rows" data-testid="ui-s3-rows-preview">
            {rows.map((item) => (
              <RecordingRow key={item.external_media_id} item={item} />
            ))}
          </ul>
          <div className="ui-s3-actions">
            <button
              type="button"
              className="ui-s3-cta ui-s3-cta--primary"
              onClick={confirmAndImport}
              disabled={Boolean(learningContextId && (!learningContext || contextError))}
              data-testid="ui-s3-confirm-import-btn"
            >
              Yes, import them
            </button>
            <button
              type="button"
              className="ui-s3-cta ui-s3-cta--ghost"
              onClick={cancelBatch}
              data-testid="ui-s3-cancel-btn"
            >
              Not yet
            </button>
          </div>
        </section>
      )}

      {(phase === "importing" || phase === "enumerating") && (
        <section className="ui-s3-importing" data-testid="ui-s3-importing-card">
          <ul className="ui-s3-rows" data-testid="ui-s3-rows-live">
            {rows.map((item) => (
              <RecordingRow key={item.external_media_id} item={item} />
            ))}
          </ul>
        </section>
      )}

      {phase === "complete" && summary && (
        <section className="ui-s3-complete" data-testid="ui-s3-complete-card">
          <dl className="ui-s3-summary">
            <div>
              <dt>Recordings filed</dt>
              <dd data-testid="ui-s3-summary-imported">
                {summary.imported_count > 0 ? (
                  <Link
                    to="/my/uploads"
                    data-testid="ui-s3-summary-imported-link"
                    aria-label={`See the ${summary.imported_count} uploaded recordings`}
                  >{summary.imported_count}</Link>
                ) : summary.imported_count}
              </dd>
            </div>
            <div>
              <dt>Already on file</dt>
              <dd data-testid="ui-s3-summary-duplicates">{summary.duplicate_count}</dd>
            </div>
            <div>
              <dt>Couldn&apos;t accept</dt>
              <dd data-testid="ui-s3-summary-refused">{summary.refused_count}</dd>
            </div>
          </dl>
          {summary.refused_count > 0 && (
            <button type="button" className="ui-s3-cta" data-testid="ui-s3-retry-btn"
              onClick={confirmAndImport}
              disabled={Boolean(learningContextId && (!learningContext || contextError))}>
              Retry interrupted uploads
            </button>
          )}
          {learningContextId && rows.filter(row => row.status === "imported" && row.mime_type?.startsWith("video/")).map(row =>
            <LearningVideoReview key={row.external_media_id} mediaId={row.external_media_id} filename={row.filename} />
          )}
          <p className="ui-s3-see-all">
            <Link to="/my/uploads" data-testid="ui-s3-see-all-uploads">See all my uploads →</Link>
          </p>
          <button
            type="button"
            className="ui-s3-cta"
            onClick={cancelBatch}
            data-testid="ui-s3-start-new-btn"
          >
            Import another batch
          </button>
        </section>
      )}
    </main>
  );
}

function guessMime(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  const map = {
    // Video — modern cameras + dashcam transport-stream formats
    mp4: "video/mp4", mov: "video/quicktime", m4v: "video/x-m4v",
    avi: "video/x-msvideo", mkv: "video/x-matroska",
    webm: "video/webm",
    ts: "video/mp2t", mts: "video/mp2t", m2ts: "video/mp2t",
    mxf: "application/mxf",
    // Audio
    mp3: "audio/mpeg", m4a: "audio/mp4", wav: "audio/wav", aac: "audio/aac",
    flac: "audio/flac", ogg: "audio/ogg",
    // Image
    jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
    heic: "image/heic", heif: "image/heif", webp: "image/webp",
    gif: "image/gif", bmp: "image/bmp", tiff: "image/tiff", tif: "image/tiff",
  };
  return map[ext] || "application/octet-stream";
}
